import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Save, Plus, Trash2, Mail, Users, Settings as SettingsIcon, Phone, Database, CheckCircle, XCircle, AlertCircle, RefreshCw, Eye, Globe, ToggleLeft, ShieldAlert, Unlock, Pencil, Check, X, Image as ImageIcon, Terminal, Copy, ClipboardCheck, Link2, Clock, List as ListIcon, Bot, KeyRound, ChevronDown, ChevronRight, FileBarChart } from 'lucide-react';
import { SMTPSettings, SmtpComSettings, HostlinksSettings, User, Contact, MySQLSettings, InterspireSettings, EmailConfigSettings, FeaturePermissions, RoleFeatures, BrandingSettings } from '../types';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getAxiosErrorMessage, logSafeAxiosError } from '../utils/safeAxiosLog';
import ReportsSettings from './admin/ReportsSettings';

type AdminTab =
  | 'email' | 'smtp' | 'emails' | 'users' | 'contacts' | 'database' | 'interspire'
  | 'email-config' | 'features' | 'security' | 'branding' | 'server-logs'
  | 'sharing' | 'list-fields' | 'ai' | 'reports';

const VALID_ADMIN_TABS = new Set<string>([
  'email', 'smtp', 'emails', 'users', 'contacts', 'database', 'interspire', 'email-config', 'features', 'security', 'branding', 'server-logs', 'sharing', 'list-fields', 'ai', 'reports',
]);

const Admin: React.FC = () => {
  const { setFeaturePermissions: setContextPermissions } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab =
    tabFromUrl && VALID_ADMIN_TABS.has(tabFromUrl)
      ? (tabFromUrl as AdminTab)
      : 'email';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);
  const [smtpSettings, setSMTPSettings] = useState<SMTPSettings>({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromEmail: ''
  });
  const [smtpComSettings, setSmtpComSettings] = useState<SmtpComSettings>({
    apiKey: '',
    enabled: false,
    chartMonths: 36,
  });
  const [hostlinksSettings, setHostlinksSettings] = useState<HostlinksSettings>({
    enabled: false,
    apiKey: '',
    baseUrl: 'https://hostlinks.grantwritingusa.com',
    ingestKey: '',
    ingestUrl: '',
  });
  const [hostlinksCopied, setHostlinksCopied] = useState<'url' | 'key' | null>(null);
  const [emailConfigSettings, setEmailConfigSettings] = useState<EmailConfigSettings>({
    sendFromName: 'Grant Writing USA',
    sendFromEmail: 'success@email.grantwritingusa.com',
    replyToEmail: 'success@email.grantwritingusa.com',
    bounceEmail: 'bounced10802443@grantwritingusa.net'
  });
  const [mysqlSettings, setMySQLSettings] = useState<MySQLSettings>({
    host: 'localhost',
    port: 3306,
    user: 'root',
    password: '',
    database: 'emailcraft',
    enabled: false
  });
  const [interspireSettings, setInterspireSettings] = useState<InterspireSettings>({
    host: 'localhost',
    port: 3306,
    user: 'root',
    password: '',
    database: 'interspire',
    enabled: false
  });
  const [databaseStatus, setDatabaseStatus] = useState<{
    enabled: boolean;
    connected: boolean;
    canConnect: boolean;
  }>({ enabled: false, connected: false, canConnect: false });
  const [interspireStatus, setInterspireStatus] = useState<{
    enabled: boolean;
    connected: boolean;
    canConnect: boolean;
  }>({ enabled: false, connected: false, canConnect: false });
  const [interspireStructure, setInterspireStructure] = useState<any[]>([]);
  const [showStructure, setShowStructure] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingInterspire, setTestingInterspire] = useState(false);
  const [initializingTables, setInitializingTables] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState<{ counts: Record<string, number>; usingMySQL: boolean } | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [previewEmails, setPreviewEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user' as 'admin' | 'manager' | 'user'
  });
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserForm, setEditUserForm] = useState<{ username: string; email: string; role: 'admin' | 'manager' | 'user' }>({ username: '', email: '', role: 'user' });
  const [editUserError, setEditUserError] = useState<string | null>(null);

  // Interspire Field Map state
  const [fieldMap, setFieldMap] = useState<{
    countyStateFieldId: number | null;
    staffMemberFieldId: number | null;
  }>({ countyStateFieldId: null, staffMemberFieldId: 27 });
  const [fieldMapCustomFields, setFieldMapCustomFields] = useState<Array<{ fieldid: number; name: string }>>([]);
  const [loadingFieldMap, setLoadingFieldMap] = useState(false);
  const [savingFieldMap, setSavingFieldMap] = useState(false);
  const [fieldMapSaved, setFieldMapSaved] = useState(false);

  const defaultRoleFeatures: RoleFeatures = { buildCampaign: true, contacts: true, emailTemplates: true, alumniImport: true, alerts: true, emailSends: true, emailQueue: true, contactsQuickActions: true, contactsBuildSegment: true, contactsEditSegment: true, contactsDeleteSegment: true, contactsImport: true, contactsManageSubscribers: true, domainLookup: true, dashboard: true, dashboardUnsubDrilldown: true, queueReviewButtons: true, queueSelectionCheckmarks: true, queueReviewModal: true, queueEditEmail: true, queueStaggerButton: false, metricsLogs: false, sendsExportRecipients: false, contactsExportList: false, contactsExportSegment: false, contactsListScoring: false, contactsAuditSegments: true, contactsUtilities: false };
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>({
    manager: { ...defaultRoleFeatures },
    user:    { ...defaultRoleFeatures },
  });
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featuresSaved, setFeaturesSaved] = useState(false);

  // Security / IP lockout state
  interface LockoutInfo {
    ip: string;
    count: number;
    banned: boolean;
    lockedUntil: number;
    firstAttemptAt: number;
    lastAttemptAt: number;
  }
  const [lockouts, setLockouts] = useState<LockoutInfo[]>([]);
  const [loadingLockouts, setLoadingLockouts] = useState(false);
  const [unblockingIp, setUnblockingIp] = useState<string | null>(null);
  const [branding, setBranding] = useState<BrandingSettings>({ logoDataUrl: '', footerText: '' });
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingMsg, setBrandingMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Server Logs
  const [serverLogs, setServerLogs] = useState<{ errorLines: string[]; outLines: string[]; fetchedAt: string | null; errorPath: string; loading: boolean; error: string | null }>({
    errorLines: [], outLines: [], fetchedAt: null, errorPath: '', loading: false, error: null,
  });
  const [serverLogLines, setServerLogLines] = useState(100);
  const [serverLogTab, setServerLogTab] = useState<'error' | 'out' | 'segments'>('error');
  const [logCopied, setLogCopied] = useState(false);
  interface SegmentAuditEntry {
    timestamp: string;
    event: 'segment_created' | 'segment_updated' | 'segment_deleted';
    userId?: string;
    username?: string;
    segmentId: number;
    segmentName: string;
    connector?: string;
    countBefore: number | null;
    countAfter: number | null;
    countDelta: number | null;
    valueCountBefore: number;
    valueCountAfter: number;
    valuesAdded: string[];
    valuesRemoved: string[];
  }
  const [segmentAudit, setSegmentAudit] = useState<{
    entries: SegmentAuditEntry[];
    path: string;
    fetchedAt: string | null;
    loading: boolean;
    error: string | null;
  }>({ entries: [], path: '', fetchedAt: null, loading: false, error: null });
  const [segmentAuditFilter, setSegmentAuditFilter] = useState('');
  const [expandedAuditKeys, setExpandedAuditKeys] = useState<Set<string>>(new Set());
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [newContact, setNewContact] = useState({
    name: '',
    details: ''
  });
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [alertRunHour, setAlertRunHour] = useState<number>(0);
  const [savingAlertSchedule, setSavingAlertSchedule] = useState(false);
  const [alertScheduleSaved, setAlertScheduleSaved] = useState(false);
  const [staggerSchedule, setStaggerSchedule] = useState({
    staggerCronEnabled: false,
    staggerRunHour: 0,
    staggerIntervalMinutes: 30 as 15 | 30 | 45 | 60,
  });
  const [savingStaggerSchedule, setSavingStaggerSchedule] = useState(false);
  const [staggerScheduleSaved, setStaggerScheduleSaved] = useState(false);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_ADMIN_TABS.has(t)) {
      setActiveTab(t as AdminTab);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSMTPSettings();
    fetchSmtpComSettings();
    fetchHostlinksSettings();
    fetchPreviewEmails();
    fetchUsers();
    fetchContacts();
    fetchMySQLSettings();
    fetchDatabaseStatus();
    fetchInterspireSettings();
    fetchInterspireStatus();
    fetchEmailConfigSettings();
    fetchFeaturePermissions();
    axios.get<{ alertRunHour: number }>('/api/settings/alert-schedule')
      .then(res => setAlertRunHour(res.data.alertRunHour))
      .catch(() => {});
    axios.get<{ staggerCronEnabled: boolean; staggerRunHour: number; staggerIntervalMinutes: 15 | 30 | 45 | 60 }>('/api/settings/stagger-schedule')
      .then(res => setStaggerSchedule(res.data))
      .catch(() => {});
  }, []);

  const fetchSMTPSettings = async () => {
    try {
      const response = await axios.get('/api/settings/smtp');
      setSMTPSettings({ ...response.data, password: response.data.password ?? '' });
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    }
  };

  const fetchSmtpComSettings = async () => {
    try {
      const response = await axios.get<SmtpComSettings>('/api/settings/smtpcom');
      setSmtpComSettings({
        enabled: !!response.data.enabled,
        apiKey: response.data.apiKey ?? '',
        chartMonths: response.data.chartMonths === 24 ? 24 : 36,
      });
    } catch (error) {
      console.error('Error fetching SMTP.com settings:', error);
    }
  };

  const applyHostlinksSettings = (data: HostlinksSettings) => {
    setHostlinksSettings({
      enabled: !!data.enabled,
      apiKey: data.apiKey ?? '',
      baseUrl: data.baseUrl || 'https://hostlinks.grantwritingusa.com',
      ingestKey: data.ingestKey ?? '',
      ingestUrl: data.ingestUrl ?? '',
    });
  };

  const fetchHostlinksSettings = async () => {
    try {
      const response = await axios.get<HostlinksSettings>('/api/settings/hostlinks');
      applyHostlinksSettings(response.data);
    } catch (error) {
      console.error('Error fetching Hostlinks settings:', error);
    }
  };

  const fetchEmailConfigSettings = async () => {
    try {
      const response = await axios.get('/api/settings/email-config');
      setEmailConfigSettings(response.data);
    } catch (error) {
      console.error('Error fetching email config settings:', error);
    }
  };

  const fetchFeaturePermissions = async () => {
    try {
      const response = await axios.get<FeaturePermissions>('/api/settings/features');
      // Merge with defaults so any keys missing from older stored data still have a value
      const merged: FeaturePermissions = {
        manager: { ...defaultRoleFeatures, ...response.data.manager },
        user:    { ...defaultRoleFeatures, ...response.data.user },
      };
      setFeaturePermissions(merged);
    } catch (error) {
      console.error('Error fetching feature permissions:', error);
    }
  };

  const handleSaveFeaturePermissions = async () => {
    setSavingFeatures(true);
    try {
      const response = await axios.put<FeaturePermissions>('/api/settings/features', featurePermissions);
      setFeaturePermissions(response.data);
      setContextPermissions(response.data);
      setFeaturesSaved(true);
      setTimeout(() => setFeaturesSaved(false), 3000);
    } catch (error: unknown) {
      console.error('Error saving feature permissions:', error);
      const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to save permissions';
      alert(msg);
    } finally {
      setSavingFeatures(false);
    }
  };

  const fetchMySQLSettings = async () => {
    try {
      const response = await axios.get('/api/database/mysql/settings');
      setMySQLSettings({ ...response.data, password: response.data.password ?? '' });
    } catch (error) {
      console.error('Error fetching MySQL settings:', error);
    }
  };

  const fetchInterspireSettings = async () => {
    try {
      const response = await axios.get('/api/interspire/settings');
      setInterspireSettings({ ...response.data, password: response.data.password ?? '' });
    } catch (error) {
      console.error('Error fetching Interspire settings:', error);
    }
  };

  const fetchDatabaseStatus = async () => {
    try {
      const response = await axios.get('/api/database/mysql/status');
      setDatabaseStatus(response.data);
    } catch (error) {
      console.error('Error fetching database status:', error);
    }
  };

  const fetchInterspireStatus = async () => {
    try {
      const response = await axios.get('/api/interspire/status');
      setInterspireStatus(response.data);
    } catch (error) {
      console.error('Error fetching Interspire status:', error);
    }
  };

  const fetchInterspireStructure = async () => {
    setLoadingStructure(true);
    try {
      const response = await axios.get('/api/interspire/structure');
      setInterspireStructure(response.data);
      setShowStructure(true);
    } catch (error: any) {
      console.error('Error fetching Interspire structure:', error);
      alert('Failed to load database structure: ' + (error.response?.data?.details || error.message));
    } finally {
      setLoadingStructure(false);
    }
  };

  const handleSaveMySQL = async () => {
    try {
      await axios.post('/api/database/mysql/settings', mysqlSettings);
      alert('MySQL settings saved successfully!');
      // Refresh status after a short delay to allow server to process
      setTimeout(() => {
        fetchDatabaseStatus();
      }, 500);
    } catch (error) {
      logSafeAxiosError('Error saving MySQL settings', error);
      alert(getAxiosErrorMessage(error, 'Error saving MySQL settings'));
    }
  };

  const handleSaveInterspire = async () => {
    try {
      await axios.post('/api/interspire/settings', interspireSettings);
      alert('Interspire settings saved successfully!');
      // Refresh status after a short delay to allow server to process
      setTimeout(() => {
        fetchInterspireStatus();
      }, 500);
    } catch (error) {
      logSafeAxiosError('Error saving Interspire settings', error);
      alert(getAxiosErrorMessage(error, 'Error saving Interspire settings'));
    }
  };

  const fetchFieldMap = async () => {
    setLoadingFieldMap(true);
    try {
      const [mapRes, fieldsRes] = await Promise.all([
        axios.get('/api/settings/interspire-field-map'),
        axios.get('/api/interspire/customfields').catch(() => ({ data: [] })),
      ]);
      setFieldMap({
        countyStateFieldId: mapRes.data.countyStateFieldId ?? null,
        staffMemberFieldId: mapRes.data.staffMemberFieldId ?? 27,
      });
      setFieldMapCustomFields(fieldsRes.data);
    } catch (error) {
      console.error('Error fetching field map:', error);
    } finally {
      setLoadingFieldMap(false);
    }
  };

  const handleSaveFieldMap = async () => {
    setSavingFieldMap(true);
    try {
      await axios.put('/api/settings/interspire-field-map', fieldMap);
      setFieldMapSaved(true);
      setTimeout(() => setFieldMapSaved(false), 3000);
    } catch (error) {
      console.error('Error saving field map:', error);
      alert('Error saving field map');
    } finally {
      setSavingFieldMap(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post('/api/database/mysql/test', mysqlSettings);
      if (response.data.success) {
        alert('Connection successful!');
      } else {
        alert('Connection failed: ' + response.data.message);
      }
      fetchDatabaseStatus();
    } catch (error: unknown) {
      logSafeAxiosError('Error testing MySQL connection', error);
      const e = error as { response?: { data?: { details?: string; error?: string } }; message?: string };
      alert(
        'Connection test failed: ' +
          (e.response?.data?.details || e.response?.data?.error || e.message || 'Unknown error')
      );
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestInterspire = async () => {
    setTestingInterspire(true);
    try {
      const response = await axios.post('/api/interspire/test', interspireSettings);
      if (response.data.success) {
        alert('Interspire connection successful!');
      } else {
        alert('Interspire connection failed: ' + response.data.message);
      }
      fetchInterspireStatus();
    } catch (error: unknown) {
      logSafeAxiosError('Error testing Interspire connection', error);
      const e = error as { response?: { data?: { details?: string; error?: string } }; message?: string };
      alert(
        'Interspire connection test failed: ' +
          (e.response?.data?.details || e.response?.data?.error || e.message || 'Unknown error')
      );
    } finally {
      setTestingInterspire(false);
    }
  };

  const handleInitializeTables = async () => {
    if (!window.confirm('This will create EmailCraft tables in your MySQL database. Continue?')) {
      return;
    }
    
    setInitializingTables(true);
    try {
      await axios.post('/api/database/mysql/init');
      alert('Database initialized successfully!');
      fetchDatabaseStatus();
    } catch (error: any) {
      console.error('Error initializing database:', error);
      alert('Database initialization failed: ' + (error.response?.data?.details || error.message));
    } finally {
      setInitializingTables(false);
    }
  };

  const handleMigrateFromJSON = async () => {
    if (!window.confirm(
      'This will copy all data from app-data.json into MySQL and switch the server to use MySQL. Continue?'
    )) return;
    setMigrating(true);
    setMigrateResult(null);
    try {
      const response = await axios.post('/api/database/mysql/migrate');
      setMigrateResult({ counts: response.data.counts, usingMySQL: response.data.usingMySQL });
    } catch (error: any) {
      console.error('Migration error:', error);
      alert('Migration failed: ' + (error.response?.data?.details || error.response?.data?.error || error.message));
    } finally {
      setMigrating(false);
    }
  };

  const fetchPreviewEmails = async () => {
    try {
      const response = await axios.get('/api/settings/preview-emails');
      setPreviewEmails(response.data);
    } catch (error) {
      console.error('Error fetching preview emails:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get('/api/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleSaveSMTP = async () => {
    try {
      await axios.post('/api/settings/smtp', smtpSettings);
      window.alert('SMTP settings saved successfully!');
    } catch (error) {
      logSafeAxiosError('Error saving SMTP settings', error);
      window.alert(getAxiosErrorMessage(error, 'Error saving SMTP settings'));
    }
  };

  const handleSaveSmtpCom = async () => {
    try {
      const res = await axios.post<SmtpComSettings>('/api/settings/smtpcom', smtpComSettings);
      setSmtpComSettings({
        enabled: !!res.data.enabled,
        apiKey: res.data.apiKey ?? '',
        chartMonths: res.data.chartMonths === 24 ? 24 : 36,
      });
      window.alert('SMTP.com API settings saved successfully!');
    } catch (error) {
      logSafeAxiosError('Error saving SMTP.com settings', error);
      window.alert(getAxiosErrorMessage(error, 'Error saving SMTP.com settings'));
    }
  };

  const handleSaveHostlinks = async () => {
    try {
      const res = await axios.post<HostlinksSettings>('/api/settings/hostlinks', {
        enabled: hostlinksSettings.enabled,
        apiKey: hostlinksSettings.apiKey,
        baseUrl: hostlinksSettings.baseUrl,
      });
      applyHostlinksSettings(res.data);
      window.alert('Hostlinks settings saved successfully!');
    } catch (error) {
      logSafeAxiosError('Error saving Hostlinks settings', error);
      window.alert(getAxiosErrorMessage(error, 'Error saving Hostlinks settings'));
    }
  };

  const copyHostlinksValue = async (which: 'url' | 'key', value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setHostlinksCopied(which);
      window.setTimeout(() => setHostlinksCopied(null), 2000);
    } catch {
      window.alert(`Copy failed. Select and copy manually:\n${value}`);
    }
  };

  const handleRegenerateHostlinksIngestKey = async () => {
    if (
      !window.confirm(
        'Regenerate the Hostlinks ingest key? The WordPress plugin must be updated with the new key or pushes will fail.'
      )
    ) {
      return;
    }
    try {
      const res = await axios.post<HostlinksSettings>('/api/settings/hostlinks/regenerate-ingest-key');
      applyHostlinksSettings(res.data);
      window.alert('Ingest key regenerated. Update the Hostlinks plugin with the new key.');
    } catch (error) {
      logSafeAxiosError('Error regenerating Hostlinks ingest key', error);
      window.alert(getAxiosErrorMessage(error, 'Error regenerating ingest key'));
    }
  };

  const handleSaveAlertSchedule = async () => {
    setSavingAlertSchedule(true);
    try {
      await axios.put('/api/settings/alert-schedule', { alertRunHour });
      setAlertScheduleSaved(true);
      setTimeout(() => setAlertScheduleSaved(false), 3000);
    } catch (error) {
      logSafeAxiosError('Error saving alert schedule', error);
      alert(getAxiosErrorMessage(error, 'Failed to save alert schedule'));
    } finally {
      setSavingAlertSchedule(false);
    }
  };

  const handleSaveStaggerSchedule = async () => {
    setSavingStaggerSchedule(true);
    try {
      await axios.put('/api/settings/stagger-schedule', staggerSchedule);
      setStaggerScheduleSaved(true);
      setTimeout(() => setStaggerScheduleSaved(false), 3000);
    } catch (error) {
      logSafeAxiosError('Error saving stagger schedule', error);
      alert(getAxiosErrorMessage(error, 'Failed to save stagger schedule'));
    } finally {
      setSavingStaggerSchedule(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    
    try {
      await axios.post('/api/settings/preview-emails', { email: newEmail });
      setPreviewEmails([...previewEmails, newEmail]);
      setNewEmail('');
    } catch (error) {
      console.error('Error adding email:', error);
      alert('Error adding email address');
    }
  };

  const handleRemoveEmail = async (email: string) => {
    try {
      await axios.delete(`/api/settings/preview-emails/${encodeURIComponent(email)}`);
      setPreviewEmails(previewEmails.filter(e => e !== email));
    } catch (error) {
      console.error('Error removing email:', error);
      alert('Error removing email address');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      alert('Please fill in all fields');
      return;
    }
    if (newUser.password.length < 8) {
      alert('Password must be at least 8 characters (same as server requirement).');
      return;
    }

    try {
      await axios.post('/api/users', {
        username: newUser.username.trim(),
        email: newUser.email.trim(),
        password: newUser.password,
        role: newUser.role,
      });
      fetchUsers();
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'user'
      });
      alert('User created successfully!');
    } catch (error) {
      logSafeAxiosError('Error creating user', error);
      alert(getAxiosErrorMessage(error, 'Error creating user'));
    }
  };

  const handleResetPassword = async (userId: string) => {
    const newPassword = prompt('Enter new password (minimum 8 characters):');
    if (!newPassword) return;
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }

    try {
      await axios.post(`/api/users/${userId}/reset-password`, { password: newPassword });
      alert('Password reset successfully!');
    } catch (error) {
      logSafeAxiosError('Error resetting password', error);
      alert(getAxiosErrorMessage(error, 'Error resetting password'));
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`/api/users/${userId}`);
      fetchUsers();
    } catch (error) {
      logSafeAxiosError('Error deleting user', error);
      alert(getAxiosErrorMessage(error, 'Error deleting user'));
    }
  };

  const handleStartEditUser = (user: User) => {
    setEditingUserId(user.id);
    setEditUserForm({ username: user.username, email: user.email, role: user.role as 'admin' | 'manager' | 'user' });
    setEditUserError(null);
  };

  const handleCancelEditUser = () => {
    setEditingUserId(null);
    setEditUserError(null);
  };

  const handleSaveEditUser = async (userId: string) => {
    setEditUserError(null);
    try {
      await axios.put(`/api/users/${userId}`, editUserForm);
      setEditingUserId(null);
      fetchUsers();
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error saving user';
      setEditUserError(msg);
    }
  };

  const handleSaveContact = async () => {
    if (!newContact.name.trim() || !newContact.details.trim()) {
      alert('Please fill in both name and details');
      return;
    }

    try {
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, newContact);
      } else {
        await axios.post('/api/contacts', newContact);
      }
      fetchContacts();
      setNewContact({ name: '', details: '' });
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Error saving contact');
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name,
      details: contact.details
    });
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await axios.delete(`/api/contacts/${contactId}`);
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact');
    }
  };

  const resetContactForm = () => {
    setNewContact({ name: '', details: '' });
    setEditingContact(null);
  };

  const handleSaveEmailConfig = async () => {
    try {
      await axios.post('/api/settings/email-config', emailConfigSettings);
      alert('Email configuration settings saved successfully!');
    } catch (error) {
      console.error('Error saving email config settings:', error);
      alert('Error saving email config settings');
    }
  };

  const fetchLockouts = useCallback(async () => {
    setLoadingLockouts(true);
    try {
      const res = await axios.get('/api/settings/lockouts');
      setLockouts(res.data);
    } catch (err) {
      console.error('Failed to load lockouts', err);
    } finally {
      setLoadingLockouts(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'security') {
      fetchLockouts();
    }
  }, [activeTab, fetchLockouts]);

  // Branding
  useEffect(() => {
    if (activeTab === 'branding') {
      axios.get<BrandingSettings>('/api/settings/branding')
        .then(r => setBranding(r.data))
        .catch(() => {});
    }
  }, [activeTab]);

  // Server Logs
  const fetchServerLogs = useCallback(async (lines?: number) => {
    const n = lines ?? serverLogLines;
    setServerLogs(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get<{
        errorLog: { path: string; exists: boolean; lines: string[] };
        outLog: { path: string; exists: boolean; lines: string[] };
        fetchedAt: string;
      }>(`/api/settings/server-logs?lines=${n}`);
      setServerLogs({
        errorLines: res.data.errorLog.lines,
        outLines: res.data.outLog.lines,
        fetchedAt: res.data.fetchedAt,
        errorPath: res.data.errorLog.path,
        loading: false,
        error: null,
      });
    } catch (e) {
      setServerLogs(prev => ({ ...prev, loading: false, error: getAxiosErrorMessage(e, 'Failed to fetch server logs') }));
    }
  }, [serverLogLines]);

  const fetchSegmentAudit = useCallback(async () => {
    setSegmentAudit(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get<{
        path: string;
        entries: SegmentAuditEntry[];
        fetchedAt: string;
      }>(`/api/settings/server-logs/segment-audit?limit=${serverLogLines}`);
      setSegmentAudit({
        entries: res.data.entries,
        path: res.data.path,
        fetchedAt: res.data.fetchedAt,
        loading: false,
        error: null,
      });
    } catch (e) {
      setSegmentAudit(prev => ({
        ...prev,
        loading: false,
        error: getAxiosErrorMessage(e, 'Failed to fetch segment change log'),
      }));
    }
  }, [serverLogLines]);

  useEffect(() => {
    if (activeTab !== 'server-logs') return;
    if (serverLogTab === 'segments') fetchSegmentAudit();
    else fetchServerLogs();
  }, [activeTab, serverLogTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveBranding = async () => {
    setBrandingSaving(true);
    setBrandingMsg(null);
    try {
      await axios.put('/api/settings/branding', branding);
      setBrandingMsg({ type: 'success', text: 'Branding saved.' });
      // Notify App.tsx to re-fetch so the header/footer updates immediately
      const refresh = (window as unknown as Record<string, unknown>).__refreshBranding;
      if (typeof refresh === 'function') (refresh as () => void)();
    } catch {
      setBrandingMsg({ type: 'error', text: 'Failed to save branding.' });
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      setBrandingMsg({ type: 'error', text: 'Logo file must be smaller than 512 KB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBranding(prev => ({ ...prev, logoDataUrl: (ev.target?.result as string) ?? '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleUnblock = async (ip: string) => {
    setUnblockingIp(ip);
    try {
      await axios.delete(`/api/settings/lockouts/${encodeURIComponent(ip)}`);
      setLockouts(prev => prev.filter(l => l.ip !== ip));
    } catch (err) {
      alert('Failed to unblock IP');
    } finally {
      setUnblockingIp(null);
    }
  };

  // ── Sharing / guest links ─────────────────────────────────────────────────
  interface GuestLinkRecord { id: string; url: string; expiresAt: number; createdAt: number; label?: string; duration?: string; }
  const [guestLinks, setGuestLinks] = useState<GuestLinkRecord[]>([]);
  const [guestLinksLoading, setGuestLinksLoading] = useState(false);
  const [guestLinksError, setGuestLinksError] = useState<string | null>(null);
  const [guestLinkGenerating, setGuestLinkGenerating] = useState(false);
  const [guestLinkDuration, setGuestLinkDuration] = useState<string>('1h');
  const [guestLinkCopied, setGuestLinkCopied] = useState<string | null>(null);
  const [guestLinkDeleting, setGuestLinkDeleting] = useState<string | null>(null);

  const fetchGuestLinks = useCallback(async () => {
    setGuestLinksLoading(true);
    setGuestLinksError(null);
    try {
      const res = await axios.get<GuestLinkRecord[]>('/api/auth/guest-links', { timeout: 8000 });
      setGuestLinks(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to load links';
      setGuestLinksError(msg);
    } finally {
      setGuestLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sharing') fetchGuestLinks();
  }, [activeTab, fetchGuestLinks]);

  // ── List Fields ────────────────────────────────────────────────────────────
  interface ListField { fieldid: number; listid: number; name: string; fieldtype: string; required: number; defaultvalue: string; }
  const [listFields, setListFields] = useState<ListField[]>([]);
  const [listFieldsDraft, setListFieldsDraft] = useState<Record<number, number>>({}); // fieldid → required (1/0)
  const [listFieldsLoading, setListFieldsLoading] = useState(false);
  const [listFieldsSaving, setListFieldsSaving] = useState(false);
  const [listFieldsMsg, setListFieldsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchListFields = useCallback(async () => {
    setListFieldsLoading(true);
    setListFieldsMsg(null);
    try {
      const res = await axios.get<ListField[]>('/api/interspire/customfields');
      setListFields(res.data);
      const draft: Record<number, number> = {};
      res.data.forEach(f => { draft[f.fieldid] = f.required; });
      setListFieldsDraft(draft);
    } catch (err: any) {
      setListFieldsMsg({ type: 'error', text: err?.response?.data?.error ?? 'Failed to load fields' });
    } finally { setListFieldsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'list-fields') fetchListFields();
  }, [activeTab, fetchListFields]);

  const saveListFields = async () => {
    setListFieldsSaving(true);
    setListFieldsMsg(null);
    try {
      const updates = Object.entries(listFieldsDraft).map(([fieldid, required]) => ({
        fieldid: Number(fieldid), required,
      }));
      await axios.put('/api/interspire/customfields/required', { updates });
      setListFieldsMsg({ type: 'success', text: 'Field requirements saved.' });
      await fetchListFields();
    } catch (err: any) {
      setListFieldsMsg({ type: 'error', text: err?.response?.data?.error ?? 'Failed to save' });
    } finally { setListFieldsSaving(false); }
  };

  // ─── AI Review state ────────────────────────────────────────────────────────
  interface GeminiModelInfo { id: string; displayName: string; description: string; }
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiKeyChanged, setAiKeyChanged] = useState(false);
  const [aiRules, setAiRules] = useState<string[]>([]);
  const [aiNewRule, setAiNewRule] = useState('');
  const [aiPrimary, setAiPrimary] = useState<string>('');
  const [aiSecondary, setAiSecondary] = useState<string>('');
  const [aiTertiary, setAiTertiary] = useState<string>('');
  const [aiCachedModels, setAiCachedModels] = useState<GeminiModelInfo[]>([]);
  const [aiLastRefreshedAt, setAiLastRefreshedAt] = useState<string | null>(null);
  const [aiAdminRefreshRequired, setAiAdminRefreshRequired] = useState(false);
  const [aiAdminRefreshReason, setAiAdminRefreshReason] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiModelsRefreshing, setAiModelsRefreshing] = useState(false);
  const [aiMsg, setAiMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchAiConfig = useCallback(async () => {
    setAiLoading(true);
    setAiMsg(null);
    try {
      const res = await axios.get<any>('/api/ai/config');
      const d = res.data;
      setAiApiKey(d.geminiApiKey ?? '');
      setAiRules(d.rules ?? []);
      setAiPrimary(d.primary ?? '');
      setAiSecondary(d.secondary ?? '');
      setAiTertiary(d.tertiary ?? '');
      setAiCachedModels(d.cachedModels ?? []);
      setAiLastRefreshedAt(d.lastModelRefreshAt ?? null);
      setAiAdminRefreshRequired(d.adminRefreshRequired ?? false);
      setAiAdminRefreshReason(d.adminRefreshReason ?? null);
      setAiKeyChanged(false);
    } catch (err: any) {
      setAiMsg({ type: 'error', text: err?.response?.data?.error ?? 'Failed to load AI config' });
    } finally { setAiLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'ai') fetchAiConfig();
  }, [activeTab, fetchAiConfig]);

  const saveAiConfig = async () => {
    setAiSaving(true);
    setAiMsg(null);
    try {
      await axios.put('/api/ai/config', {
        geminiApiKey: aiKeyChanged ? aiApiKey : '••••••••',
        rules: aiRules,
        primary: aiPrimary || null,
        secondary: aiSecondary || null,
        tertiary: aiTertiary || null,
      });
      setAiMsg({ type: 'success', text: 'AI settings saved.' });
      setAiKeyChanged(false);
      await fetchAiConfig();
    } catch (err: any) {
      setAiMsg({ type: 'error', text: err?.response?.data?.error ?? 'Failed to save AI config' });
    } finally { setAiSaving(false); }
  };

  const refreshAiModels = async () => {
    setAiModelsRefreshing(true);
    setAiMsg(null);
    try {
      const res = await axios.post<{ models: GeminiModelInfo[]; lastModelRefreshAt: string }>('/api/ai/models/refresh');
      setAiCachedModels(res.data.models);
      setAiLastRefreshedAt(res.data.lastModelRefreshAt);
      setAiAdminRefreshRequired(false);
      setAiAdminRefreshReason(null);
      setAiMsg({ type: 'success', text: `${res.data.models.length} models loaded.` });
    } catch (err: any) {
      setAiMsg({ type: 'error', text: err?.response?.data?.error ?? 'Failed to refresh models' });
    } finally { setAiModelsRefreshing(false); }
  };

  const DURATION_LABELS: Record<string, string> = {
    '1h': '1 Hour',
    '24h': '24 Hours',
    '7d': '7 Days',
    '30d': '30 Days',
    '90d': '90 Days',
    permanent: '90 Days',
  };

  const generateGuestLink = async () => {
    setGuestLinkGenerating(true);
    try {
      const res = await axios.post<GuestLinkRecord>('/api/auth/guest-link', { duration: guestLinkDuration });
      setGuestLinks((prev) => [res.data, ...prev]);
    } catch {
      window.alert('Failed to generate guest link. Please try again.');
    } finally {
      setGuestLinkGenerating(false);
    }
  };

  const copyGuestLink = async (id: string, url: string) => {
    await navigator.clipboard.writeText(url);
    setGuestLinkCopied(id);
    setTimeout(() => setGuestLinkCopied(null), 2000);
  };

  const deleteGuestLink = async (id: string) => {
    if (!window.confirm('Remove this guest link from the list?')) return;
    setGuestLinkDeleting(id);
    try {
      await axios.delete(`/api/auth/guest-links/${id}`);
      setGuestLinks((prev) => prev.filter((l) => l.id !== id));
    } catch {
      window.alert('Failed to delete guest link.');
    } finally {
      setGuestLinkDeleting(null);
    }
  };

  const tabs = [
    { id: 'email' as const, name: 'Email', icon: Mail },
    { id: 'database' as const, name: 'Database', icon: Database },
    { id: 'interspire' as const, name: 'INTERSPIRE', icon: Database },
    { id: 'contacts' as const, name: 'Contacts', icon: Phone },
    { id: 'users' as const, name: 'Users', icon: Users },
    { id: 'features' as const, name: 'Features', icon: ToggleLeft },
    { id: 'sharing' as const, name: 'Sharing', icon: Link2 },
    { id: 'list-fields' as const, name: 'List Fields', icon: ListIcon },
    { id: 'ai' as const, name: 'AI', icon: Bot },
    { id: 'branding' as const, name: 'Branding', icon: ImageIcon },
    { id: 'reports' as const, name: 'Reports', icon: FileBarChart },
    { id: 'security' as const, name: 'Security', icon: ShieldAlert },
    { id: 'server-logs' as const, name: 'Server Logs', icon: Terminal },
  ];

  return (
    <div className="px-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Administration</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage system settings, email configurations, databases, contacts, and user accounts.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8">
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as typeof activeTab)}
            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      // Auto-refresh status when Database or Interspire tabs are clicked
                      if (tab.id === 'database') {
                        setTimeout(() => fetchDatabaseStatus(), 100);
                      } else if (tab.id === 'interspire') {
                        setTimeout(() => fetchInterspireStatus(), 100);
                        setTimeout(() => fetchFieldMap(), 200);
                      }
                    }}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-8">
        {(activeTab === 'email' || activeTab === 'smtp' || activeTab === 'emails' || activeTab === 'email-config') && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

            {/* ── LEFT COLUMN: SMTP + Alert Schedule ── */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">SMTP Configuration</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">SMTP Host</label>
                    <input
                      type="text"
                      value={smtpSettings.host}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Port</label>
                    <input
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, port: parseInt(e.target.value) || smtpSettings.port || 0 })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">SMTP Username</label>
                    <input
                      type="text"
                      value={smtpSettings.user}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, user: e.target.value })}
                      placeholder="username (not necessarily an email)"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">May differ from email address</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">SMTP Password</label>
                    <input
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, password: e.target.value })}
                      placeholder="App password or email password"
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">From Email Address</label>
                    <input
                      type="email"
                      value={smtpSettings.fromEmail}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, fromEmail: e.target.value })}
                      placeholder="sender@yourdomain.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">Appears as the sender on outgoing emails</p>
                  </div>
                  <div className="sm:col-span-2 flex items-center">
                    <input
                      type="checkbox"
                      id="secure"
                      checked={smtpSettings.secure}
                      onChange={(e) => setSMTPSettings({ ...smtpSettings, secure: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="secure" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">Use TLS/SSL</label>
                  </div>
                </div>
                <div className="mt-5">
                  <button
                    onClick={handleSaveSMTP}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save SMTP Settings
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">SMTP.com API</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                  Powers the bounce-rate chart on the Dashboard. Separate from the SMTP relay above.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="smtpcom-enabled"
                      checked={smtpComSettings.enabled}
                      onChange={(e) => setSmtpComSettings({ ...smtpComSettings, enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="smtpcom-enabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
                      Enable SMTP.com stats on Dashboard
                    </label>
                  </div>
                  <div>
                    <label htmlFor="smtpcom-chart-months" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Chart window
                    </label>
                    <select
                      id="smtpcom-chart-months"
                      value={smtpComSettings.chartMonths === 24 ? 24 : 36}
                      onChange={(e) =>
                        setSmtpComSettings({
                          ...smtpComSettings,
                          chartMonths: Number(e.target.value) === 24 ? 24 : 36,
                        })
                      }
                      className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    >
                      <option value={24}>24 months</option>
                      <option value={36}>36 months</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      Changing the window clears the stats cache and backfills on the next dashboard load.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">API Key</label>
                    <input
                      type="password"
                      value={smtpComSettings.apiKey}
                      onChange={(e) => setSmtpComSettings({ ...smtpComSettings, apiKey: e.target.value })}
                      placeholder={smtpComSettings.apiKey === '••••••••' ? 'API key saved (hidden)' : 'Paste SMTP.com API key'}
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      From SMTP.com → Account → API Keys. Leave as bullets to keep the existing key.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveSmtpCom}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save SMTP.com API
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Hostlinks</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                  EmailCraft does not pull Hostlinks from the VPS (Imunify / Cloudflare blocks that). The Hostlinks
                  WordPress plugin should POST upcoming events to the ingest URL below. Sync via browser on Build
                  remains as a manual fallback. The X-HL-Key is only needed for that browser sync.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hostlinks-enabled"
                      checked={hostlinksSettings.enabled}
                      onChange={(e) => setHostlinksSettings({ ...hostlinksSettings, enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="hostlinks-enabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
                      Enable Hostlinks browser sync
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Base URL</label>
                    <input
                      type="text"
                      value={hostlinksSettings.baseUrl}
                      onChange={(e) => setHostlinksSettings({ ...hostlinksSettings, baseUrl: e.target.value })}
                      placeholder="https://hostlinks.grantwritingusa.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">API Key (X-HL-Key)</label>
                    <input
                      type="password"
                      value={hostlinksSettings.apiKey}
                      onChange={(e) => setHostlinksSettings({ ...hostlinksSettings, apiKey: e.target.value })}
                      placeholder={hostlinksSettings.apiKey === '••••••••' ? 'API key saved (hidden)' : 'Paste Hostlinks X-HL-Key'}
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      From Hostlinks → Settings → Automation API (GET scope). Leave as bullets to keep the existing key.
                    </p>
                  </div>
                  <div className="rounded-md border border-gray-200 dark:border-slate-600 p-4 space-y-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Plugin ingest</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      Give these to the Hostlinks plugin. Auth header is{' '}
                      <code className="text-[11px]">X-EmailCraft-Ingest-Key</code> (not X-HL-Key). Each POST replaces
                      the stored event list.
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Ingest URL</label>
                      <div className="mt-1 flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={hostlinksSettings.ingestUrl || ''}
                          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white font-mono text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => void copyHostlinksValue('url', hostlinksSettings.ingestUrl || '')}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                          {hostlinksCopied === 'url' ? <ClipboardCheck className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                          <span className="ml-1">{hostlinksCopied === 'url' ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Ingest key</label>
                      <div className="mt-1 flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={hostlinksSettings.ingestKey || ''}
                          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white font-mono text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => void copyHostlinksValue('key', hostlinksSettings.ingestKey || '')}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                          {hostlinksCopied === 'key' ? <ClipboardCheck className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                          <span className="ml-1">{hostlinksCopied === 'key' ? 'Copied' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleRegenerateHostlinksIngestKey()}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-slate-600 text-xs font-medium rounded-md text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Regenerate ingest key
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveHostlinks}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Hostlinks
                  </button>
                </div>
              </div>

              {/* Alert Automation Schedule */}
              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Alert Automation Schedule</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Set the time the daily alert check runs on the server (stored in UTC).</p>
                <div className="flex items-end gap-6 flex-wrap">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Run time (UTC)</label>
                    <select
                      value={alertRunHour}
                      onChange={e => setAlertRunHour(Number(e.target.value))}
                      className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Your local time</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {new Date(Date.UTC(2000, 0, 1, alertRunHour)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSaveAlertSchedule}
                      disabled={savingAlertSchedule}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {savingAlertSchedule ? 'Saving...' : 'Save Schedule'}
                    </button>
                    {alertScheduleSaved && (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Saved
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Queue Send Stagger (nightly) */}
              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Queue Send Stagger</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">
                  When enabled, runs nightly at the UTC time below. Finds days where multiple sends share the same hour and reschedules them from 6:00 AM Pacific in the configured interval. Off by default — use Queue → Stagger send times to test manually first.
                </p>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={staggerSchedule.staggerCronEnabled}
                      onChange={e => setStaggerSchedule(s => ({ ...s, staggerCronEnabled: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable nightly automatic stagger</span>
                  </label>
                  <div className="flex items-end gap-6 flex-wrap">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Run time (UTC)</label>
                      <select
                        value={staggerSchedule.staggerRunHour}
                        onChange={e => setStaggerSchedule(s => ({ ...s, staggerRunHour: Number(e.target.value) }))}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Interval between sends</label>
                      <select
                        value={staggerSchedule.staggerIntervalMinutes}
                        onChange={e => setStaggerSchedule(s => ({
                          ...s,
                          staggerIntervalMinutes: Number(e.target.value) as 15 | 30 | 45 | 60,
                        }))}
                        className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSaveStaggerSchedule}
                        disabled={savingStaggerSchedule}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {savingStaggerSchedule ? 'Saving...' : 'Save Stagger Settings'}
                      </button>
                      {staggerScheduleSaved && (
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Saved
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN: Email Config + Preview Emails ── */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">Email Configuration</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Send From Name</label>
                    <input
                      type="text"
                      value={emailConfigSettings.sendFromName}
                      onChange={(e) => setEmailConfigSettings({ ...emailConfigSettings, sendFromName: e.target.value })}
                      placeholder="Grant Writing USA"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Send From Email</label>
                    <input
                      type="email"
                      value={emailConfigSettings.sendFromEmail}
                      onChange={(e) => setEmailConfigSettings({ ...emailConfigSettings, sendFromEmail: e.target.value })}
                      placeholder="success@email.grantwritingusa.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reply To Email</label>
                    <input
                      type="email"
                      value={emailConfigSettings.replyToEmail}
                      onChange={(e) => setEmailConfigSettings({ ...emailConfigSettings, replyToEmail: e.target.value })}
                      placeholder="success@email.grantwritingusa.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bounce Email</label>
                    <input
                      type="email"
                      value={emailConfigSettings.bounceEmail}
                      onChange={(e) => setEmailConfigSettings({ ...emailConfigSettings, bounceEmail: e.target.value })}
                      placeholder="bounced10802443@grantwritingusa.net"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <button
                    onClick={handleSaveEmailConfig}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Email Configuration
                  </button>
                </div>
              </div>

              {/* Preview Emails */}
              <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Preview Email Addresses</h3>
                <div className="mb-4">
                  <div className="flex space-x-3">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <button
                      onClick={handleAddEmail}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {previewEmails.map((email) => (
                    <div key={email} className="flex items-center justify-between p-3 border dark:border-slate-600 rounded-lg">
                      <span className="text-sm text-gray-900 dark:text-gray-200">{email}</span>
                      <button onClick={() => handleRemoveEmail(email)} className="text-red-600 hover:text-red-900">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {previewEmails.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-slate-400 py-4 text-center">No preview email addresses configured</p>
                  )}
                </div>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'database' && (
          <div className="space-y-6">
            {/* Database Status */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Database Status</h3>
                <button
                  onClick={fetchDatabaseStatus}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center">
                  {databaseStatus.enabled ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {databaseStatus.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center">
                  {databaseStatus.canConnect ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {databaseStatus.canConnect ? 'Can Connect' : 'Cannot Connect'}
                  </span>
                </div>
                <div className="flex items-center">
                  {databaseStatus.connected ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {databaseStatus.connected ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
              </div>
            </div>

            {/* MySQL Configuration */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">MySQL Database Configuration</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="mysqlEnabled"
                    checked={mysqlSettings.enabled}
                    onChange={(e) => setMySQLSettings({ ...mysqlSettings, enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="mysqlEnabled" className="ml-2 block text-sm text-gray-900 font-medium">
                    Enable MySQL Database
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Host
                    </label>
                    <input
                      type="text"
                      value={mysqlSettings.host}
                      onChange={(e) => setMySQLSettings({ ...mysqlSettings, host: e.target.value })}
                      placeholder="localhost"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Port
                    </label>
                    <input
                      type="number"
                      value={mysqlSettings.port}
                      onChange={(e) => setMySQLSettings({ ...mysqlSettings, port: parseInt(e.target.value) || mysqlSettings.port || 0 })}
                      placeholder="3306"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Username
                    </label>
                    <input
                      type="text"
                      value={mysqlSettings.user}
                      onChange={(e) => setMySQLSettings({ ...mysqlSettings, user: e.target.value })}
                      placeholder="root"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      type="password"
                      value={mysqlSettings.password}
                      onChange={(e) => setMySQLSettings({ ...mysqlSettings, password: e.target.value })}
                      placeholder="Enter database password"
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Database Name
                    </label>
                    <input
                      type="text"
                      value={mysqlSettings.database}
                      onChange={(e) => setMySQLSettings({ ...mysqlSettings, database: e.target.value })}
                      placeholder="emailcraft"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Make sure this database exists in your MySQL server
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    onClick={handleSaveMySQL}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </button>
                  
                  <button
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                  </button>
                  
                  <button
                    onClick={handleInitializeTables}
                    disabled={initializingTables || !mysqlSettings.enabled}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {initializingTables ? 'Initializing...' : 'Initialize Tables'}
                  </button>

                  <button
                    onClick={handleMigrateFromJSON}
                    disabled={migrating || !mysqlSettings.enabled}
                    className="inline-flex items-center px-4 py-2 border border-indigo-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    {migrating ? 'Migrating...' : 'Migrate from JSON'}
                  </button>
                </div>

                {migrateResult && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium">Migration complete — server is now using MySQL.</p>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {Object.entries(migrateResult.counts).map(([k, v]) => (
                            <li key={k}>{k}: {v} record{v !== 1 ? 's' : ''}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-700">
                      <p className="font-medium">Database Integration Notes:</p>
                      <ul className="mt-2 space-y-1 list-disc list-inside">
                        <li>Make sure MySQL server is running and accessible</li>
                        <li>The specified database must exist before testing connection</li>
                        <li>Initialize Tables creates the <code>ec_*</code> EmailCraft tables in MySQL</li>
                        <li>Migrate from JSON copies all data from app-data.json into MySQL and switches the server live</li>
                        <li>Restart the server after migrating to confirm MySQL is used on cold boot</li>
                        <li>To roll back: disable MySQL and restart — the server falls back to app-data.json automatically</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'interspire' && (
          <div className="space-y-6">
            {/* Interspire Status */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Interspire Database Status</h3>
                <button
                  onClick={fetchInterspireStatus}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center">
                  {interspireStatus.enabled ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {interspireStatus.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center">
                  {interspireStatus.canConnect ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {interspireStatus.canConnect ? 'Can Connect' : 'Cannot Connect'}
                  </span>
                </div>
                <div className="flex items-center">
                  {interspireStatus.connected ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                  )}
                  <span className="text-sm">
                    {interspireStatus.connected ? 'Connected' : 'Not Connected'}
                  </span>
                </div>
              </div>
            </div>

            {/* Interspire Configuration */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Interspire Mailer Database Configuration</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="interspireEnabled"
                    checked={interspireSettings.enabled}
                    onChange={(e) => setInterspireSettings({ ...interspireSettings, enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="interspireEnabled" className="ml-2 block text-sm text-gray-900 font-medium">
                    Enable Interspire Database Connection
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Host
                    </label>
                    <input
                      type="text"
                      value={interspireSettings.host}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, host: e.target.value })}
                      placeholder="localhost"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Port
                    </label>
                    <input
                      type="number"
                      value={interspireSettings.port}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, port: parseInt(e.target.value) || interspireSettings.port || 0 })}
                      placeholder="3306"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Username
                    </label>
                    <input
                      type="text"
                      value={interspireSettings.user}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, user: e.target.value })}
                      placeholder="interspire_user"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      type="password"
                      value={interspireSettings.password}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, password: e.target.value })}
                      placeholder="Enter Interspire database password"
                      autoComplete="new-password"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Database Name
                    </label>
                    <input
                      type="text"
                      value={interspireSettings.database}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, database: e.target.value })}
                      placeholder="interspire"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter your existing Interspire Mailer database name
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Sends Cache Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={1440}
                      value={interspireSettings.sendHistoryCacheMins ?? 60}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, sendHistoryCacheMins: parseInt(e.target.value) || 0 })}
                      placeholder="60"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      How long to cache the Email Sends list before re-querying the database. 0 = always fresh. Default: 60 minutes.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Queue Cache Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={1440}
                      value={interspireSettings.queueCacheMins ?? 60}
                      onChange={(e) => setInterspireSettings({ ...interspireSettings, queueCacheMins: parseInt(e.target.value) || 0 })}
                      placeholder="60"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                      How long to cache the Email Queue list before re-querying the database. 0 = always fresh. Default: 60 minutes.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    onClick={handleSaveInterspire}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </button>
                  
                  <button
                    onClick={handleTestInterspire}
                    disabled={testingInterspire}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    {testingInterspire ? 'Testing...' : 'Test Connection'}
                  </button>
                  
                  <button
                    onClick={fetchInterspireStructure}
                    disabled={loadingStructure || !interspireSettings.enabled}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {loadingStructure ? 'Loading...' : 'View Database Structure'}
                  </button>
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">Interspire Integration Notes:</p>
                      <ul className="mt-2 space-y-1 list-disc list-inside">
                        <li>This connects to your existing Interspire Mailer database</li>
                        <li>Make sure the Interspire database exists and is accessible</li>
                        <li>Use the same credentials you use for Interspire Mailer</li>
                        <li>View Database Structure to explore the Interspire tables</li>
                        <li>We'll use this to store and manage email campaigns</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Field mapping: County/State + Staff Member */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Health Check Field Mapping</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Select which Interspire custom field stores the <strong>County, State</strong> value used by the Segment Health Check.
                      Each Interspire install may use a different field ID.
                    </p>
                  </div>
                  <button
                    onClick={fetchFieldMap}
                    disabled={loadingFieldMap}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${loadingFieldMap ? 'animate-spin' : ''}`} />
                    Refresh Fields
                  </button>
                </div>

                {loadingFieldMap ? (
                  <div className="text-sm text-gray-500 py-4">Loading custom fields…</div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        County / State Field
                      </label>
                      <select
                        value={fieldMap.countyStateFieldId ?? ''}
                        onChange={(e) =>
                          setFieldMap({
                            ...fieldMap,
                            countyStateFieldId: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="">— Auto-detect from segment rules —</option>
                        {fieldMapCustomFields.map((f) => (
                          <option key={f.fieldid} value={f.fieldid}>
                            Field {f.fieldid} — {f.name}
                          </option>
                        ))}
                      </select>
                      {fieldMapCustomFields.length === 0 && (
                        <p className="mt-1 text-xs text-amber-600">
                          No custom fields loaded. Make sure Interspire is connected and click Refresh Fields.
                        </p>
                      )}
                      {fieldMap.countyStateFieldId != null && (
                        <p className="mt-1 text-xs text-green-700">
                          Pinned to Field {fieldMap.countyStateFieldId}
                          {fieldMapCustomFields.find((f) => f.fieldid === fieldMap.countyStateFieldId)
                            ? ` — ${fieldMapCustomFields.find((f) => f.fieldid === fieldMap.countyStateFieldId)!.name}`
                            : ''}
                        </p>
                      )}
                      {fieldMap.countyStateFieldId == null && (
                        <p className="mt-1 text-xs text-gray-500">
                          Auto-detect: the field with the most rules in each segment&apos;s searchinfo will be used.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Bulk Unsubscribe Notes</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Custom field used for the note written by <strong>Bulk Unsubscribe</strong> (and optionally cleared by Bulk Resubscribe).
                    Default is Field 27 (Staff Member).
                  </p>
                </div>

                {loadingFieldMap ? (
                  <div className="text-sm text-gray-500 py-4">Loading custom fields…</div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Staff Member Field
                      </label>
                      <select
                        value={fieldMap.staffMemberFieldId ?? 27}
                        onChange={(e) =>
                          setFieldMap({
                            ...fieldMap,
                            staffMemberFieldId: e.target.value === '' ? 27 : Number(e.target.value),
                          })
                        }
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        {fieldMapCustomFields.map((f) => (
                          <option key={f.fieldid} value={f.fieldid}>
                            Field {f.fieldid} — {f.name}
                          </option>
                        ))}
                        {fieldMapCustomFields.length === 0 && (
                          <option value={27}>Field 27 — Staff Member (default)</option>
                        )}
                      </select>
                      <p className="mt-1 text-xs text-green-700">
                        Using Field {fieldMap.staffMemberFieldId ?? 27}
                        {fieldMapCustomFields.find((f) => f.fieldid === (fieldMap.staffMemberFieldId ?? 27))
                          ? ` — ${fieldMapCustomFields.find((f) => f.fieldid === (fieldMap.staffMemberFieldId ?? 27))!.name}`
                          : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:col-span-2 flex items-center gap-4">
                <button
                  onClick={handleSaveFieldMap}
                  disabled={savingFieldMap}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingFieldMap ? 'Saving…' : 'Save Field Map'}
                </button>
                {fieldMapSaved && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Saved
                  </span>
                )}
              </div>
            </div>

            {/* Database Structure Viewer */}
            {showStructure && (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Interspire Database Structure</h3>
                  <button
                    onClick={() => setShowStructure(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {interspireStructure.map((table) => (
                    <div key={table.name} className="mb-6 border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Table: {table.name}</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Column</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Type</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Null</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Key</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Default</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {table.columns.map((column: any, index: number) => (
                              <tr key={index}>
                                <td className="px-3 py-2 font-mono text-xs">{column.Field}</td>
                                <td className="px-3 py-2 text-xs">{column.Type}</td>
                                <td className="px-3 py-2 text-xs">{column.Null}</td>
                                <td className="px-3 py-2 text-xs">{column.Key}</td>
                                <td className="px-3 py-2 text-xs">{column.Default || 'NULL'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {activeTab === 'contacts' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Contact Management</h3>
            
            {/* Add/Edit Contact Form */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder="e.g., John Smith - Event Coordinator"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Contact Details
                  </label>
                  <textarea
                    rows={4}
                    value={newContact.details}
                    onChange={(e) => setNewContact({ ...newContact, details: e.target.value })}
                    placeholder="Phone: (555) 123-4567&#10;Email: john@company.com&#10;Office: Room 301&#10;Hours: Mon-Fri 9AM-5PM"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter contact information on separate lines (phone, email, office, hours, etc.)
                  </p>
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button
                  onClick={handleSaveContact}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingContact ? 'Update Contact' : 'Add Contact'}
                </button>
                {editingContact && (
                  <button
                    onClick={resetContactForm}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Contacts List */}
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 whitespace-pre-line max-w-xs">
                          {contact.details}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contacts.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No contacts found
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">User Management</h3>
            
            {/* Add User Form */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-4">Add New User</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Username"
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="Email"
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Password (min 8 characters)"
                  autoComplete="new-password"
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'admin' | 'manager' | 'user' })}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="mt-4">
                <button
                  onClick={handleAddUser}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </button>
              </div>
            </div>

            {/* Users List */}
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {editUserError && (
                    <tr>
                      <td colSpan={5} className="px-6 py-2 bg-red-50 text-red-700 text-sm">
                        {editUserError}
                      </td>
                    </tr>
                  )}
                  {users.map((user) => {
                    const isEditing = editingUserId === user.id;
                    return (
                      <tr key={user.id} className={isEditing ? 'bg-blue-50' : undefined}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editUserForm.username}
                              onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
                              className="w-full rounded border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          ) : (
                            <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="email"
                              value={editUserForm.email}
                              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                              className="w-full rounded border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{user.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <select
                              value={editUserForm.role}
                              onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as 'admin' | 'manager' | 'user' })}
                              className="rounded border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="user">User</option>
                              <option value="manager">Manager</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-purple-100 text-purple-800'
                                : user.role === 'manager'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.role}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleSaveEditUser(user.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                              >
                                <Check className="h-3.5 w-3.5" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelEditUser}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => handleStartEditUser(user)}
                                className="text-gray-500 hover:text-blue-700"
                                title="Edit user"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(user.id)}
                                className="text-blue-600 hover:text-blue-900 text-sm"
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No users found
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === 'features' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900">Feature Access by Role</h3>
              <p className="mt-1 text-sm text-gray-500">
                Control which features each role can access. Admins always have full access.
                Disabled features are hidden from the nav and blocked at the URL level.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="py-3 text-left text-sm font-semibold text-gray-900 dark:text-slate-100 w-1/2">Feature</th>
                    <th className="py-3 text-center text-sm font-semibold text-gray-900 dark:text-slate-100 w-1/4">
                      <div className="flex flex-col items-center gap-1">
                        <span>Manager</span>
                        <span className="text-xs font-normal text-gray-500 dark:text-slate-400">Elevated access</span>
                      </div>
                    </th>
                    <th className="py-3 text-center text-sm font-semibold text-gray-900 dark:text-slate-100 w-1/4">
                      <div className="flex flex-col items-center gap-1">
                        <span>User</span>
                        <span className="text-xs font-normal text-gray-500 dark:text-slate-400">Standard access</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {(
                    [
                      { key: 'buildCampaign' as keyof RoleFeatures, label: 'Build Campaign', description: 'Email campaign builder, template selector, and Build from Event', indent: false },
                      { key: 'contacts' as keyof RoleFeatures, label: 'Contacts', description: 'Contact management, lists, and Interspire segments', indent: false },
                      { key: 'contactsQuickActions' as keyof RoleFeatures, label: '↳ Quick Actions', description: 'Quick-action buttons on the Overview tab', indent: true },
                      { key: 'contactsBuildSegment' as keyof RoleFeatures, label: '↳ Build New Segment', description: 'Button to create a new segment on the Segments tab', indent: true },
                      { key: 'contactsEditSegment' as keyof RoleFeatures, label: '↳ Edit Segment', description: 'Edit button on each segment row', indent: true },
                      { key: 'contactsDeleteSegment' as keyof RoleFeatures, label: '↳ Delete Segment', description: 'Delete button on each segment row', indent: true },
                      { key: 'contactsImport' as keyof RoleFeatures, label: '↳ Import Tab', description: 'Contact import / ZeroBounce clean & import tab', indent: true },
                      { key: 'contactsManageSubscribers' as keyof RoleFeatures, label: '↳ Manage Subscribers', description: 'Add Subscriber button and per-row suspend / edit / delete actions in List view', indent: true },
                      { key: 'domainLookup' as keyof RoleFeatures, label: 'Domain Lookup', description: 'Segment-based domain investigation tool (/domain-lookup)' },
                      { key: 'emailTemplates' as keyof RoleFeatures, label: 'Email Templates', description: 'Create and manage email HTML templates', indent: false },
                      { key: 'alumniImport' as keyof RoleFeatures, label: 'Alumni Import', description: 'CVENT alumni data import tool', indent: false },
                      { key: 'alerts' as keyof RoleFeatures, label: 'Alerts', description: 'Unsubscribe and bounce rate threshold alerts', indent: false },
                      { key: 'dashboard' as keyof RoleFeatures, label: 'Dashboard', description: 'CEO/marketing KPI dashboard with send stats and queue overview (/dashboard)', indent: false },
                      { key: 'dashboardUnsubDrilldown' as keyof RoleFeatures, label: '↳ Unsub chart send drill-down', description: 'Click a month on the Interspire Unsubscribes chart to see top sends by volume (with elevated-rate badges)', indent: true },
                      { key: 'emailSends' as keyof RoleFeatures, label: 'Sends', description: 'Email send history with segment details', indent: false },
                      { key: 'emailQueue' as keyof RoleFeatures, label: 'Queue', description: 'Scheduled emails not yet sent (/queue)', indent: false },
                      { key: 'queueReviewButtons' as keyof RoleFeatures, label: '↳ Review Buttons', description: 'AI-powered review toolbar (Run New / Run Checked / Re-run All)', indent: true },
                      { key: 'queueSelectionCheckmarks' as keyof RoleFeatures, label: '↳ Selection Checkmarks', description: 'Row checkboxes used to select jobs for Run Checked', indent: true },
                      { key: 'queueReviewModal' as keyof RoleFeatures, label: '↳ Review Modal', description: 'Clickable review status icons that open the per-rule results modal', indent: true },
                      { key: 'queueEditEmail' as keyof RoleFeatures, label: '↳ Edit Email', description: 'Edit button in the queue email preview — saves subject and HTML back to Interspire', indent: true },
                      { key: 'queueStaggerButton' as keyof RoleFeatures, label: '↳ Stagger Send Times', description: 'Stagger Send Times button — reschedules collision-day sends to spread them out from 6:00 AM Pacific', indent: true },
                      { key: 'metricsLogs' as keyof RoleFeatures, label: '↳ Metrics & Logs', description: 'Mail Ops status strip on Queue page and full Postfix/SMTP metrics page', indent: true },
                      { key: 'sendsExportRecipients' as keyof RoleFeatures, label: '↳ Export Recipients CSV', description: 'Date-range recipient export button on the Sends page (unique emails from recent campaigns)', indent: true },
                      { key: 'contactsExportList' as keyof RoleFeatures, label: '↳ Export List CSV', description: 'Export CSV button on list detail view in Contacts (email, first name, last name)', indent: true },
                      { key: 'contactsExportSegment' as keyof RoleFeatures, label: '↳ Export Segment CSV', description: 'Export CSV button on Contacts → Segments (email, names, County/State, subscribe date, list ID)', indent: true },
                      { key: 'contactsListScoring' as keyof RoleFeatures, label: '↳ Segment Scoring', description: 'Heuristic risk scoring for a selected segment — never-opened/bounced addresses for bulk unsubscribe', indent: true },
                      { key: 'contactsAuditSegments' as keyof RoleFeatures, label: '↳ Audit Segments', description: 'Compare pasted County/State lists to segment rules; mark verified', indent: true },
                      { key: 'contactsUtilities' as keyof RoleFeatures, label: '↳ Utilities', description: 'Contacts → Utilities tab (CSV Hydrate, Mass Unsub, Bulk Update, MX Lookup)', indent: true },
                    ]
                  ).map(({ key, label, description, indent }) => (
                    <tr key={key} className={indent ? 'bg-gray-50/60 hover:bg-gray-100/60 dark:bg-slate-800/50 dark:hover:bg-slate-700/60' : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'}>
                      <td className={`py-3 pr-4 ${indent ? 'pl-6' : ''}`}>
                        <div className={`text-sm font-medium ${indent ? 'text-gray-600 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100'}`}>{label}</div>
                        <div className="text-xs text-gray-500 dark:text-slate-500 mt-0.5">{description}</div>
                      </td>
                      <td className="py-3 text-center">
                        <input
                          type="checkbox"
                          checked={featurePermissions.manager[key] ?? true}
                          onChange={(e) => setFeaturePermissions({
                            ...featurePermissions,
                            manager: { ...featurePermissions.manager, [key]: e.target.checked }
                          })}
                          className="h-4 w-4 rounded border-gray-300 dark:border-slate-500 dark:bg-slate-700 text-blue-600 accent-blue-500 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3 text-center">
                        <input
                          type="checkbox"
                          checked={featurePermissions.user[key] ?? true}
                          onChange={(e) => setFeaturePermissions({
                            ...featurePermissions,
                            user: { ...featurePermissions.user, [key]: e.target.checked }
                          })}
                          className="h-4 w-4 rounded border-gray-300 dark:border-slate-500 dark:bg-slate-700 text-blue-600 accent-blue-500 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/40">
                    <td className="py-3 text-xs text-gray-500 dark:text-slate-400 italic">Admin role always has full access to all features</td>
                    <td className="py-3 text-center">
                      <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
                    </td>
                    <td className="py-3 text-center">
                      <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={handleSaveFeaturePermissions}
                disabled={savingFeatures}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingFeatures ? 'Saving...' : 'Save Permissions'}
              </button>
              {featuresSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Saved — permissions updated immediately
                </span>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && <ReportsSettings />}

        {activeTab === 'branding' && (
          <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Branding</h2>
            <p className="text-sm text-gray-500 mb-6">
              Customise the header logo and footer text shown throughout the app.
            </p>

            {brandingMsg && (
              <div className={`mb-4 flex items-center gap-2 rounded-md px-4 py-3 text-sm ${brandingMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {brandingMsg.type === 'success' ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <XCircle className="h-4 w-4 flex-shrink-0" />}
                {brandingMsg.text}
              </div>
            )}

            {/* Logo */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Header Logo</label>
              <p className="text-xs text-gray-500 mb-3">Upload an image to replace the default "Z" icon in the top-left corner. Max 512 KB. PNG or SVG recommended.</p>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-16 h-16 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                  {branding.logoDataUrl ? (
                    <img src={branding.logoDataUrl} alt="Logo preview" className="h-14 w-14 object-contain" />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-xl leading-none">Z</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50">
                    <ImageIcon className="h-4 w-4" />
                    Choose image…
                    <input type="file" accept="image/*" className="sr-only" onChange={handleLogoFile} />
                  </label>
                  {branding.logoDataUrl && (
                    <button
                      type="button"
                      onClick={() => setBranding(prev => ({ ...prev, logoDataUrl: '' }))}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                    >
                      <X className="h-3 w-3" /> Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Text */}
            <div className="mb-8">
              <label htmlFor="footerText" className="block text-sm font-medium text-gray-700 mb-2">Footer Prefix Text</label>
              <p className="text-xs text-gray-500 mb-2">
                Text entered here will appear to the left of "ZENTO Email Craft | www.zentosaas.com | v…" in the footer.
                Leave blank to show only the default footer.
              </p>
              <input
                id="footerText"
                type="text"
                maxLength={200}
                value={branding.footerText}
                onChange={e => setBranding(prev => ({ ...prev, footerText: e.target.value }))}
                placeholder="e.g. Grant Writing USA Email Tools powered by"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {branding.footerText && (
                <p className="mt-2 text-xs text-gray-400 italic">
                  Preview: <span className="text-gray-600">{branding.footerText} ZENTO Email Craft | www.zentosaas.com | v…</span>
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleSaveBranding}
              disabled={brandingSaving}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {brandingSaving ? 'Saving…' : 'Save Branding'}
            </button>
          </div>
        )}

        {activeTab === 'server-logs' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Server Logs</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {serverLogTab === 'segments'
                      ? 'Durable history of segment create / update / delete from EmailCraft (not cleared with PM2 logs).'
                      : 'Tail of the PM2 error log — useful for diagnosing runtime errors without SSH.'}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-3 flex-wrap">
                  <label className="text-sm text-gray-600 dark:text-slate-300">Lines:</label>
                  <select
                    value={serverLogLines}
                    onChange={e => {
                      const n = parseInt(e.target.value, 10);
                      setServerLogLines(n);
                      if (serverLogTab === 'segments') {
                        // fetch uses serverLogLines via callback; set state then fetch with n
                        axios.get(`/api/settings/server-logs/segment-audit?limit=${n}`)
                          .then(res => setSegmentAudit({
                            entries: res.data.entries,
                            path: res.data.path,
                            fetchedAt: res.data.fetchedAt,
                            loading: false,
                            error: null,
                          }))
                          .catch(err => setSegmentAudit(prev => ({
                            ...prev,
                            loading: false,
                            error: getAxiosErrorMessage(err, 'Failed to fetch segment change log'),
                          })));
                      } else {
                        fetchServerLogs(n);
                      }
                    }}
                    className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {serverLogTab !== 'segments' && (
                    <>
                      <button
                        onClick={() => {
                          const content = serverLogTab === 'error'
                            ? serverLogs.errorLines.join('\n')
                            : serverLogs.outLines.join('\n');
                          navigator.clipboard.writeText(content).then(() => {
                            setLogCopied(true);
                            setTimeout(() => setLogCopied(false), 2000);
                          });
                        }}
                        disabled={serverLogs.loading}
                        className="inline-flex items-center gap-2 rounded-md bg-gray-100 dark:bg-slate-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                        title="Copy log to clipboard"
                      >
                        {logCopied
                          ? <><ClipboardCheck className="h-4 w-4 text-green-500" /> Copied!</>
                          : <><Copy className="h-4 w-4" /> Copy</>
                        }
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Clear the PM2 log files on the server? This cannot be undone. Segment Changes history is kept.')) return;
                          try {
                            await axios.post('/api/settings/server-logs/clear');
                            setServerLogs(prev => ({ ...prev, errorLines: [], outLines: [], fetchedAt: null }));
                          } catch {
                            window.alert('Failed to clear server log files. Check file permissions.');
                          }
                        }}
                        disabled={serverLogs.loading}
                        className="inline-flex items-center gap-2 rounded-md bg-gray-100 dark:bg-slate-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 transition-colors"
                        title="Clear server PM2 log files"
                      >
                        <Trash2 className="h-4 w-4" />
                        Clear Server Logs
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => serverLogTab === 'segments' ? fetchSegmentAudit() : fetchServerLogs()}
                    disabled={serverLogTab === 'segments' ? segmentAudit.loading : serverLogs.loading}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${(serverLogTab === 'segments' ? segmentAudit.loading : serverLogs.loading) ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {(serverLogTab === 'segments' ? segmentAudit.fetchedAt : serverLogs.fetchedAt) && (
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-3">
                  Fetched at {new Date((serverLogTab === 'segments' ? segmentAudit.fetchedAt : serverLogs.fetchedAt)!).toLocaleString()}
                  {serverLogTab === 'segments' && segmentAudit.path
                    ? <> &nbsp;·&nbsp; {segmentAudit.path}</>
                    : serverLogTab !== 'segments'
                      ? <> &nbsp;·&nbsp; {serverLogs.errorPath}</>
                      : null}
                </p>
              )}

              {/* Sub-tabs: Error / Out / Segment Changes */}
              <div className="flex gap-4 border-b border-gray-200 dark:border-slate-600 mb-3">
                {([
                  { id: 'error' as const, label: 'Error Log' },
                  { id: 'out' as const, label: 'Output Log' },
                  { id: 'segments' as const, label: 'Segment Changes' },
                ]).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setServerLogTab(t.id)}
                    className={`pb-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      serverLogTab === t.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700'
                    }`}
                  >
                    {t.label}
                    {t.id === 'error' && serverLogs.errorLines.length > 0 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
                        {serverLogs.errorLines.length}
                      </span>
                    )}
                    {t.id === 'segments' && segmentAudit.entries.length > 0 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                        {segmentAudit.entries.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {serverLogTab === 'segments' ? (
                <>
                  {segmentAudit.error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-3">
                      {segmentAudit.error}
                    </div>
                  )}
                  <div className="mb-3">
                    <input
                      type="search"
                      value={segmentAuditFilter}
                      onChange={e => setSegmentAuditFilter(e.target.value)}
                      placeholder="Filter by segment name or ID…"
                      className="w-full max-w-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {segmentAudit.loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400">
                      <RefreshCw className="h-6 w-6 animate-spin mr-2" /> Loading…
                    </div>
                  ) : (() => {
                    const q = segmentAuditFilter.trim().toLowerCase();
                    const rows = q
                      ? segmentAudit.entries.filter(e =>
                          e.segmentName.toLowerCase().includes(q) ||
                          String(e.segmentId).includes(q) ||
                          (e.username || '').toLowerCase().includes(q)
                        )
                      : segmentAudit.entries;
                    if (rows.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-slate-400 py-8 text-center">
                          No segment changes logged yet. Edits made in EmailCraft will appear here.
                        </p>
                      );
                    }
                    const fmtCount = (n: number | null) => (n == null ? '—' : n.toLocaleString());
                    const eventLabel = (ev: SegmentAuditEntry['event']) =>
                      ev === 'segment_created' ? 'Created' : ev === 'segment_deleted' ? 'Deleted' : 'Updated';
                    return (
                      <div className="overflow-x-auto border border-gray-200 dark:border-slate-600 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600 text-sm">
                          <thead className="bg-gray-50 dark:bg-slate-700/50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-slate-300 w-8" />
                              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-slate-300">When</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-slate-300">Event</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-slate-300">User</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-slate-300">Segment</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600 dark:text-slate-300">Before</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600 dark:text-slate-300">After</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600 dark:text-slate-300">Δ</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-600 dark:text-slate-300">+ / −</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                            {rows.map((row, idx) => {
                              const key = `${row.timestamp}-${row.segmentId}-${idx}`;
                              const open = expandedAuditKeys.has(key);
                              const delta = row.countDelta;
                              return (
                                <React.Fragment key={key}>
                                  <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                                    <td className="px-3 py-2">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedAuditKeys(prev => {
                                          const next = new Set(prev);
                                          if (next.has(key)) next.delete(key);
                                          else next.add(key);
                                          return next;
                                        })}
                                        className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"
                                        title="Show value diffs"
                                      >
                                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </button>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-slate-300">
                                      {new Date(row.timestamp).toLocaleString()}
                                    </td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                        row.event === 'segment_created'
                                          ? 'bg-green-50 text-green-700 border border-green-100'
                                          : row.event === 'segment_deleted'
                                            ? 'bg-red-50 text-red-700 border border-red-100'
                                            : 'bg-blue-50 text-blue-700 border border-blue-100'
                                      }`}>
                                        {eventLabel(row.event)}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-700 dark:text-slate-200">
                                      {row.username || row.userId || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="font-medium text-gray-900 dark:text-white">{row.segmentName}</div>
                                      <div className="text-xs text-gray-400">ID {row.segmentId}{row.connector ? ` · ${row.connector.toUpperCase()}` : ''}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtCount(row.countBefore)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtCount(row.countAfter)}</td>
                                    <td className={`px-3 py-2 text-right font-mono font-semibold ${
                                      delta == null ? 'text-gray-400' : delta < 0 ? 'text-red-600' : delta > 0 ? 'text-green-600' : 'text-gray-500'
                                    }`}>
                                      {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`}
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                                      <span className="text-green-600">+{row.valuesAdded.length}</span>
                                      {' / '}
                                      <span className="text-red-600">−{row.valuesRemoved.length}</span>
                                    </td>
                                  </tr>
                                  {open && (
                                    <tr className="bg-gray-50 dark:bg-slate-900/40">
                                      <td colSpan={9} className="px-4 py-3 text-xs">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <div>
                                            <div className="font-semibold text-green-700 dark:text-green-400 mb-1">
                                              Added ({row.valuesAdded.length})
                                            </div>
                                            {row.valuesAdded.length === 0 ? (
                                              <p className="text-gray-400">None</p>
                                            ) : (
                                              <ul className="max-h-40 overflow-auto space-y-0.5 text-gray-700 dark:text-slate-300">
                                                {row.valuesAdded.map(v => <li key={v}>{v}</li>)}
                                              </ul>
                                            )}
                                          </div>
                                          <div>
                                            <div className="font-semibold text-red-700 dark:text-red-400 mb-1">
                                              Removed ({row.valuesRemoved.length})
                                            </div>
                                            {row.valuesRemoved.length === 0 ? (
                                              <p className="text-gray-400">None</p>
                                            ) : (
                                              <ul className="max-h-40 overflow-auto space-y-0.5 text-gray-700 dark:text-slate-300">
                                                {row.valuesRemoved.map(v => <li key={v}>{v}</li>)}
                                              </ul>
                                            )}
                                          </div>
                                        </div>
                                        <p className="mt-2 text-gray-400">
                                          Rule values: {row.valueCountBefore} → {row.valueCountAfter}
                                        </p>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {serverLogs.error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-3">
                      {serverLogs.error}
                    </div>
                  )}

                  {serverLogs.loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400">
                      <RefreshCw className="h-6 w-6 animate-spin mr-2" /> Loading…
                    </div>
                  ) : (
                    <div className="relative">
                      <pre className="bg-gray-950 dark:bg-gray-900 text-green-400 dark:text-green-300 rounded-lg p-4 text-xs font-mono overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">
                        {serverLogTab === 'error'
                          ? (serverLogs.errorLines.length > 0
                              ? serverLogs.errorLines.join('\n')
                              : '(no error log entries)')
                          : (serverLogs.outLines.length > 0
                              ? serverLogs.outLines.join('\n')
                              : '(no output log entries)')
                        }
                      </pre>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-1 text-right">
                        Scroll to bottom for most recent entries
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">IP Lockouts &amp; Bans</h3>
                <p className="mt-1 text-sm text-gray-500">
                  IPs are temporarily locked after 3 failed logins (30 s), locked longer after 6 (5 min),
                  and permanently banned after 9. Use this panel to review and unblock them.
                </p>
              </div>
              <button
                onClick={fetchLockouts}
                disabled={loadingLockouts}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingLockouts ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingLockouts ? (
              <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
            ) : lockouts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No locked or banned IPs right now.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">IP Address</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Attempts</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Last Attempt</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lockouts.map(entry => {
                      const remainingMs = entry.banned ? null : Math.max(0, entry.lockedUntil - Date.now());
                      const remainingLabel = entry.banned
                        ? 'Banned'
                        : remainingMs !== null && remainingMs >= 60_000
                          ? `${Math.ceil(remainingMs / 60_000)}m remaining`
                          : remainingMs !== null
                            ? `${Math.ceil(remainingMs / 1_000)}s remaining`
                            : 'Expired';
                      return (
                        <tr key={entry.ip} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-gray-900">{entry.ip}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{entry.count}</td>
                          <td className="px-4 py-3 text-center">
                            {entry.banned ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                <XCircle className="h-3 w-3" /> Banned
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                <AlertCircle className="h-3 w-3" /> {remainingLabel}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {new Date(entry.lastAttemptAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleUnblock(entry.ip)}
                              disabled={unblockingIp === entry.ip}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                              <Unlock className="h-3 w-3" />
                              {unblockingIp === entry.ip ? 'Unblocking…' : 'Unblock'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {activeTab === 'sharing' && (
          <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
            <div className="flex items-start justify-between mb-1 gap-4">
              <h2 className="text-lg font-semibold text-gray-900 mt-1">Shareable View Links</h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  value={guestLinkDuration}
                  onChange={(e) => setGuestLinkDuration(e.target.value)}
                  disabled={guestLinkGenerating}
                  className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="1h">1 Hour</option>
                  <option value="24h">24 Hours</option>
                  <option value="7d">7 Days</option>
                  <option value="30d">30 Days</option>
                  <option value="90d">90 Days</option>
                </select>
                <button
                  onClick={generateGuestLink}
                  disabled={guestLinkGenerating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {guestLinkGenerating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  {guestLinkGenerating ? 'Generating…' : 'Generate'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Links give read-only access to <strong>Sends</strong> and <strong>Queue</strong> without login.
              Revoking a link here takes effect immediately. Longest duration is 90 days.
            </p>

            {guestLinksLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <RefreshCw className="h-4 w-4 animate-spin" /> Loading saved links…
              </div>
            ) : guestLinksError ? (
              <div className="flex items-center gap-3 py-4">
                <span className="text-sm text-red-500">{guestLinksError}</span>
                <button onClick={fetchGuestLinks} className="text-xs text-blue-600 underline hover:text-blue-800">Retry</button>
              </div>
            ) : guestLinks.length === 0 ? (
              <p className="text-sm text-gray-400 italic py-4">No links generated yet. Click "Generate New Link" to create one.</p>
            ) : (
              <div className="space-y-3">
                {guestLinks.map((link) => {
                  const isLongLived = link.duration === '90d' || link.duration === 'permanent';
                  const isExpired = link.expiresAt < Date.now();
                  const expiryLabel = isExpired
                    ? 'Expired'
                    : `Expires ${new Date(link.expiresAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}`;
                  const durationLabel = DURATION_LABELS[link.duration ?? '1h'] ?? link.duration ?? '';
                  return (
                    <div
                      key={link.id}
                      className={`rounded-md border px-4 py-3 ${isExpired ? 'border-gray-200 bg-gray-50' : isLongLived ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${isExpired ? 'text-gray-400' : isLongLived ? 'text-green-700' : 'text-blue-700'}`}>
                          <Clock className="h-3.5 w-3.5" />
                          {expiryLabel}
                          {durationLabel && <span className="ml-1 font-normal opacity-70">({durationLabel})</span>}
                        </span>
                        <span className="text-xs text-gray-400">{new Date(link.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={link.url}
                          className={`flex-1 rounded border px-2 py-1.5 text-xs font-mono focus:outline-none ${isExpired ? 'border-gray-200 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-700'}`}
                          onFocus={(e) => e.target.select()}
                        />
                        <button
                          onClick={() => copyGuestLink(link.id, link.url)}
                          disabled={isExpired}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Copy link"
                        >
                          {guestLinkCopied === link.id ? <><ClipboardCheck className="h-3.5 w-3.5 text-green-600" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                        </button>
                        <button
                          onClick={() => deleteGuestLink(link.id)}
                          disabled={guestLinkDeleting === link.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-red-200 rounded text-xs text-red-600 bg-white hover:bg-red-50 disabled:opacity-40"
                          title="Remove link"
                        >
                          {guestLinkDeleting === link.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-4 text-xs text-gray-400">Expired links remain listed until you remove them. Only active links grant access.</p>
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-5xl">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <Bot className="h-5 w-5 text-indigo-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Email Review</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Configure Gemini AI to review queued emails against your custom rules before sending.
            </p>

            {aiLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><RefreshCw className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : (
              <div className="space-y-5">

                {/* Admin refresh warning — full width */}
                {aiAdminRefreshRequired && (
                  <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-700/40 dark:bg-amber-900/20 px-4 py-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Model Refresh Required</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                        {aiAdminRefreshReason || 'One or more configured Gemini models may no longer be available.'} Refresh the model list and reselect models below.
                      </p>
                    </div>
                  </div>
                )}

                {/* Two-column body */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* ── Left column: Settings ── */}
                  <div className="space-y-5">

                    {/* API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <KeyRound className="inline h-4 w-4 mr-1 text-gray-400" />Gemini API Key
                      </label>
                      <input
                        type="password"
                        value={aiApiKey}
                        onChange={(e) => { setAiApiKey(e.target.value); setAiKeyChanged(true); }}
                        placeholder={aiApiKey === '••••••••' ? 'API key saved (hidden)' : 'Enter Gemini API key…'}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Get a free key at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="underline text-indigo-500">Google AI Studio</a>.</p>
                    </div>

                    {/* Model Refresh + selectors */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Gemini Review Models</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Primary → Secondary → Tertiary failover order.
                          </p>
                        </div>
                        <button
                          onClick={refreshAiModels}
                          disabled={aiModelsRefreshing || !aiApiKey}
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors flex-shrink-0"
                          title={!aiApiKey ? 'Save an API key first' : 'Fetch current model list from Gemini'}
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${aiModelsRefreshing ? 'animate-spin' : ''}`} />
                          {aiModelsRefreshing ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>

                      {aiLastRefreshedAt ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Refreshed {new Date(aiLastRefreshedAt).toLocaleString()}
                          {aiCachedModels.length > 0 && ` · ${aiCachedModels.length} models`}
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400">Not loaded yet — click Refresh after saving your API key.</p>
                      )}

                      {(['Primary', 'Secondary', 'Tertiary'] as const).map((tier) => {
                        const val = tier === 'Primary' ? aiPrimary : tier === 'Secondary' ? aiSecondary : aiTertiary;
                        const setter = tier === 'Primary' ? setAiPrimary : tier === 'Secondary' ? setAiSecondary : setAiTertiary;
                        const otherVals = [
                          tier !== 'Primary' ? aiPrimary : '',
                          tier !== 'Secondary' ? aiSecondary : '',
                          tier !== 'Tertiary' ? aiTertiary : '',
                        ].filter(Boolean);
                        return (
                          <div key={tier} className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">{tier}</span>
                            <select
                              value={val}
                              onChange={(e) => setter(e.target.value)}
                              className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value="">— None —</option>
                              {aiCachedModels.length > 0
                                ? aiCachedModels.map((m) => (
                                    <option key={m.id} value={m.id} disabled={otherVals.includes(m.id)}>
                                      {m.displayName || m.id}
                                    </option>
                                  ))
                                : val ? <option value={val}>{val}</option> : null}
                            </select>
                            {val && (
                              <button onClick={() => setter('')} className="text-gray-400 hover:text-red-500 flex-shrink-0" title="Clear">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Message */}
                    {aiMsg && (
                      <div className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${aiMsg.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>
                        {aiMsg.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        {aiMsg.text}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={saveAiConfig}
                        disabled={aiSaving}
                        className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {aiSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {aiSaving ? 'Saving…' : 'Save Settings'}
                      </button>
                      <button
                        onClick={fetchAiConfig}
                        disabled={aiLoading}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                      >
                        <RefreshCw className={`h-4 w-4 ${aiLoading ? 'animate-spin' : ''}`} /> Reload
                      </button>
                    </div>
                  </div>

                  {/* ── Right column: Rules ── */}
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Review Rules</label>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Each rule is a plain-text instruction. The AI returns pass/fail + a short note per rule.</p>

                    {aiRules.length === 0 && (
                      <p className="text-sm text-gray-400 italic mb-2">No rules yet. Add your first rule below.</p>
                    )}

                    <div className="flex-1 space-y-2 mb-3 overflow-y-auto max-h-80">
                      {aiRules.map((rule, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-md px-3 py-2">
                          <span className="flex-shrink-0 text-xs font-mono text-gray-400 mt-0.5 w-5 text-right">{idx + 1}.</span>
                          <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{rule}</span>
                          <button
                            onClick={() => setAiRules((prev) => prev.filter((_, i) => i !== idx))}
                            className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove rule"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2 mt-auto">
                      <input
                        type="text"
                        value={aiNewRule}
                        onChange={(e) => setAiNewRule(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && aiNewRule.trim()) {
                            setAiRules((prev) => [...prev, aiNewRule.trim()]);
                            setAiNewRule('');
                          }
                        }}
                        placeholder="Type a rule and press Enter…"
                        className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => {
                          if (aiNewRule.trim()) {
                            setAiRules((prev) => [...prev, aiNewRule.trim()]);
                            setAiNewRule('');
                          }
                        }}
                        disabled={!aiNewRule.trim()}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                      >
                        <Plus className="h-4 w-4" /> Add
                      </button>
                    </div>
                  </div>

                </div>{/* end two-column grid */}
              </div>
            )}
          </div>
        )}

        {activeTab === 'list-fields' && (
          <div className="bg-white rounded-lg shadow p-6 max-w-3xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Custom Field Requirements</h2>
            <p className="text-sm text-gray-500 mb-5">
              Toggle the <strong>Required</strong> switch on each field to control whether it must be filled when adding a new contact. Changes are saved directly to Interspire.
            </p>

            {/* Fields table */}
            {listFieldsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><RefreshCw className="h-4 w-4 animate-spin" /> Loading fields…</div>
            ) : listFields.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">No custom fields found. Verify Interspire is connected.</p>
            ) : listFields.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600 w-8">#</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Field Name</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Default Value</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-600">Required</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {listFields.map(field => {
                        const isRequired = (listFieldsDraft[field.fieldid] ?? field.required) === 1;
                        return (
                          <tr key={field.fieldid} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-400">{field.fieldid}</td>
                            <td className="px-4 py-3 font-medium text-gray-800">{field.name}</td>
                            <td className="px-4 py-3 text-gray-500 capitalize">{field.fieldtype}</td>
                            <td className="px-4 py-3 text-gray-400 truncate max-w-[160px]">{field.defaultvalue || <span className="italic text-gray-300">—</span>}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => setListFieldsDraft(prev => ({ ...prev, [field.fieldid]: isRequired ? 0 : 1 }))}
                                className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isRequired ? 'bg-indigo-600' : 'bg-gray-200'}`}
                                role="switch"
                                aria-checked={isRequired}
                              >
                                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isRequired ? 'translate-x-5' : 'translate-x-0'}`} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Status message */}
                {listFieldsMsg && (
                  <div className={`mt-4 flex items-center gap-2 text-sm rounded-md px-3 py-2 ${listFieldsMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {listFieldsMsg.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {listFieldsMsg.text}
                  </div>
                )}

                <div className="mt-5 flex items-center gap-3">
                  <button
                    onClick={saveListFields}
                    disabled={listFieldsSaving}
                    className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
                  >
                    {listFieldsSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {listFieldsSaving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => fetchListFields()}
                    disabled={listFieldsLoading}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${listFieldsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </>
            ) : null}
          </div>
        )}

      </div>
    </div>
  );
};

export default Admin; 